export type TwoFactorPurpose = 'login' | 'enable' | 'disable';

/** Resposta do verify quando a pessoa marcou "Confiar nesse dispositivo". */
export type TwoFactorVerifyResult = {
  deviceId: string;
  deviceToken: string;
  expiresAt: string;
};

export type TrustedDevice = {
  id: string;
  /** Rótulo montado pelo backend a partir do user agent, ex.: "Chrome no Windows". */
  label: string;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
};

export const TWO_FACTOR_ERROR = {
  REQUIRED: `TWO_FACTOR_REQUIRED`,
  INVALID_CODE: `TWO_FACTOR_INVALID_CODE`,
  CODE_EXPIRED: `TWO_FACTOR_CODE_EXPIRED`,
  TOO_MANY_ATTEMPTS: `TWO_FACTOR_TOO_MANY_ATTEMPTS`,
  TOO_MANY_REQUESTS: `TWO_FACTOR_TOO_MANY_REQUESTS`,
  RESEND_COOLDOWN: `TWO_FACTOR_RESEND_COOLDOWN`,
  ALREADY_ENABLED: `TWO_FACTOR_ALREADY_ENABLED`,
  NOT_ENABLED: `TWO_FACTOR_NOT_ENABLED`,
  ADMIN_FORBIDDEN: `TWO_FACTOR_ADMIN_FORBIDDEN`,
  ADMIN_SELF_TARGET: `TWO_FACTOR_ADMIN_SELF_TARGET`,
  ADMIN_UPDATE_FAILED: `TWO_FACTOR_ADMIN_UPDATE_FAILED`,
  DEVICE_INVALID: `TWO_FACTOR_DEVICE_INVALID`,
  DEVICE_UPDATE_FAILED: `TWO_FACTOR_DEVICE_UPDATE_FAILED`,
} as const;

export const TWO_FACTOR_ERROR_MESSAGE: Record<string, string> = {
  [TWO_FACTOR_ERROR.INVALID_CODE]: `Código inválido. Confira e tente de novo.`,
  [TWO_FACTOR_ERROR.CODE_EXPIRED]: `Esse código expirou. Peça um novo.`,
  [TWO_FACTOR_ERROR.TOO_MANY_ATTEMPTS]: `Tentativas demais. Peça um novo código.`,
  [TWO_FACTOR_ERROR.TOO_MANY_REQUESTS]: `Você pediu códigos demais. Tente novamente mais tarde.`,
  [TWO_FACTOR_ERROR.RESEND_COOLDOWN]: `Aguarde um instante antes de pedir outro código.`,
  [TWO_FACTOR_ERROR.ALREADY_ENABLED]: `A verificação em duas etapas já está ativa.`,
  [TWO_FACTOR_ERROR.NOT_ENABLED]: `A verificação em duas etapas não está ativa.`,
  [TWO_FACTOR_ERROR.ADMIN_FORBIDDEN]: `Apenas a equipe Astri pode alterar a verificação em duas etapas de outro usuário.`,
  [TWO_FACTOR_ERROR.ADMIN_SELF_TARGET]: `Para alterar a sua própria verificação em duas etapas, use o seu perfil — lá o código de confirmação é exigido.`,
  [TWO_FACTOR_ERROR.ADMIN_UPDATE_FAILED]: `Não foi possível alterar a verificação em duas etapas. Tente novamente.`,
  [TWO_FACTOR_ERROR.DEVICE_INVALID]: `Esse dispositivo não é mais confiável. Vamos pedir o código.`,
  [TWO_FACTOR_ERROR.DEVICE_UPDATE_FAILED]: `Não foi possível atualizar os dispositivos confiáveis. Tente novamente.`,
};

// O backend devolve o codigo em {"message":"CODIGO"} — formato do
// DefaultHTTPErrorHandler do Echo.
export const getTwoFactorErrorCode = (error: unknown): string | null => {
  const response = (error as { response?: { data?: { message?: unknown } } })?.response;
  const message = response?.data?.message;

  return typeof message === `string` ? message : null;
};

// isTwoFactorRequiredError isola a decisao mais perigosa do login: distinguir "o
// gate esta pedindo o codigo" de "a sessao morreu". Errar para o lado errado
// desloga o usuario num laco do qual ele nunca sai.
//
// Vive aqui, como funcao pura, porque o Login.tsx nao tem suite propria e montar
// uma exigiria mockar react-query, firebase, router e stores. Assim a regra fica
// testavel de verdade, sem teatro.
//
// O 403 e deliberado: o interceptor global do axios faz logout + redirect em TODO
// 401 (src/lib/axios/index.ts), entao um gate com 401 derrubaria a sessao antes do
// card aparecer.
export const isTwoFactorRequiredError = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;

  return status === 403 && getTwoFactorErrorCode(error) === TWO_FACTOR_ERROR.REQUIRED;
};
